import { Dispatch, FC, SetStateAction } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Button, Card, CardContent, FormHelperText, Typography, TextField, Grid } from '@mui/material';
import * as Yup from 'yup'
import { UIFormState, usePlainStateFormUtils } from '@/components/input/usePlainStateFormUtils';
import Create from '@/controller/Create';
import WithSave from '@/controller/WithSave';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import { ResetPasswordReservationResult } from '@/gen/api/user';
import { executeRecaptcha } from '@/sideEffects/recaptcha';


export enum Status {
  Init,
  InputAdminOTP,
  CollectNewPass,
}
interface S1 {
  email: string;
  otp: string;
  cid: string;
  nonce: string;
  [k: string]: string;
}
type S2 = UIFormState<S1, {}> & {phoneNotAwailable: boolean; status: Status;}

const emailSchema = 
  Yup.object().shape({
    email: Yup.string().max(255)
      .email('You must use a valid email address.')
      .required('Email is required')
  })

const PasswordRecoveryBody: FC<{states: S2, setStates: Dispatch<SetStateAction<S2>>}> = ({
  states,
  setStates,
}) => {
  const { validate, fieldProps } = usePlainStateFormUtils(states, setStates, emailSchema)

  const dispatch = useDispatch()

  const startPasswordReset = async (save: (record: any, callbacks?: any)=>void) => {
    const errors = validate(states.stage)
    if (errors) {
      setStates(v => ({...v, errors, touched: {...v.touched, email: true}}))
      return
    }
    setStates(v => ({...v, errors: {}}))
    
    const recaptchaResponse = await executeRecaptcha('resetpassword')
    save({ email: states.stage.email, recaptchaResponse }, {
      onSuccess: ({data}: {data:ResetPasswordReservationResult}) => {
        if (!data.byAdminOTP) {
          dispatch(push(`/reset-password-sent?email=${states.stage.email}`))
        } else {
          setStates(v => ({...v, 
            stage: {...v.stage, cid: data.correlationId ?? '' }, 
            prevData: {...v.prevData, email: v.stage.email}, 
            status: Status.InputAdminOTP
          }))
        }
      },
      onFailure: (err: any) => {
        let errMsg: string = err?.message
        if (errMsg === 'PhoneNotAwailable') {
          setStates(v => ({...v, phoneNotAwailable: true}))
          errMsg = 'You do not have a verified phone number in your profile. Please contact your system administrator to help reset your OnData account password.'
        }
        setStates(v => ({...v, errors: {...v.errors, submit: errMsg }}))
      },
    })
  }
  const submitOTP = (save: (record: any, callbacks?: any)=>void) => {
    setStates(v => ({...v, errors: {}}))
    const payload = { username: states.stage.email, otp: states.stage.otp, cid: states.stage.cid }
    save(payload, {
      onSuccess: ({data}: {data:{nonce:string}}) => {
        setStates(v => ({...v, stage: {...v.stage, nonce: data.nonce}, status: Status.CollectNewPass }))
      },
      onFailure: (err: any) => {
        const errMsg: string = err?.message
        setStates(v => ({...v, errors: {...v.errors, submit: errMsg }}))
      },
    })
  }
  return (
    <>
      <Helmet>
        <title>Password Recovery | OnData</title>
      </Helmet>
          
      <Card role="application" aria-labelledby="pass-recover-title" aria-describedby="pass-recover-description">
        <CardContent sx={{ display: 'flex', flexDirection: 'column', p: 4 }} >
          <Box sx={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between', mb: 3 }} >
            <div>
              <Typography color="textPrimary" gutterBottom variant="h4" id="pass-recover-title" >
                Password Recovery
              </Typography>
              <Typography color="textSecondary" variant="body2" id="pass-recover-description" >
                Forgot your password? We can help! <br/>
                {states.status !== Status.InputAdminOTP
                  ? "Please enter your email address to reset your password."
                  : "Please enter the One-Time Passcode from your system administrator."}
              </Typography>
            </div>
          </Box>
          <Box sx={{ flexGrow: 1, mt: 3 }} >
            <form>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField label="Email Address" name="email" type="email" fullWidth autoFocus={states.status === Status.Init}
                  {...(states.status === Status.Init ? fieldProps('email') : {value: states.stage.email})}
                />
              </Grid>
              {states.status === Status.InputAdminOTP &&
                <Grid item xs={12}>
                  <TextField label="One-Time Passcode" name="email" fullWidth autoFocus
                    {...fieldProps('otp')}
                  />
                </Grid>}
              {states.errors.submit && (
                <Box sx={{ mt: 3, ml: 4 }}>
                  <FormHelperText error>
                    {states.errors.submit}
                  </FormHelperText>
                </Box>
              )}
              <Grid item xs={12}>
                <Box sx={{ mt: 3 }}>
                  {states.status === Status.Init &&
                    <Create resource="resetpassword" basePath="" >
                      <WithSave 
                        render={({save, saving}) =>
                          <Button color="primary" fullWidth size="large" type="submit" variant="contained"
                            disabled={saving || states.phoneNotAwailable}
                            onClick={(e) => {startPasswordReset(save); e.preventDefault()}}
                          >
                            Submit
                          </Button>
                        }
                      />
                    </Create>}
                  {states.status === Status.InputAdminOTP &&
                    <Create resource="restpassword-otp" basePath="" >
                      <WithSave 
                        render={({save, saving}) =>
                          <Button color="primary" fullWidth size="large" type="submit" variant="contained"
                            disabled={saving || !states.stage.otp}
                            onClick={() => submitOTP(save)}
                          >
                            Submit
                          </Button>
                        }
                      />
                    </Create>}
                </Box>
              </Grid>
            </Grid>
            </form>
          </Box>
        </CardContent>
      </Card>
    
    </>
  );
};

export default PasswordRecoveryBody;
